<script setup lang="ts">
import { createPlaceholderUser, guest, userInjectionKey, type User } from '#shared'
import { computed, inject, unref } from 'vue'
import type { Message, Reaction } from '#entities/message'
import ReactionsItem from './reactions-item.vue'

const { message } = defineProps<{ message: Message }>()

interface FlattenedReaction {
  char: string
  senders: User[]
  currentUserReaction?: Reaction
}

const currentUser = inject(userInjectionKey)

const flattenedReactions = computed(() => {
  const currentUserVal = unref(currentUser)

  const flattenedReactionByChar = new Map<string, FlattenedReaction>()

  for (const reaction of message.reactions) {
    const flattenedReaction: FlattenedReaction = flattenedReactionByChar.get(reaction.char) ?? {
      char: reaction.char,
      senders: [],
    }

    if (currentUserVal !== guest && reaction.sender?.id == currentUserVal?.id) {
      flattenedReaction.currentUserReaction = reaction
    }

    flattenedReaction.senders.push(reaction.sender ?? createPlaceholderUser())
    flattenedReactionByChar.set(reaction.char, flattenedReaction)
  }

  return Array.from(flattenedReactionByChar.values())
})
</script>

<template>
  <TransitionGroup
    :class="$style.root"
    :enterActiveClass="$style.reactionAppearEnterActive"
    :leaveActiveClass="$style.reactionAppearLeaveActive"
    :enterFromClass="$style.reactionAppearEnterFrom"
    :leaveToClass="$style.reactionAppearLeaveTo"
    tag="div"
  >
    <ReactionsItem
      v-for="reaction in flattenedReactions"
      :key="reaction.char"
      :chatId="message.chatId"
      :messageId="message.id"
      :char="reaction.char"
      :senders="reaction.senders"
      :currentUserReaction="reaction.currentUserReaction"
      :disabled="!currentUser || currentUser === guest"
    />
  </TransitionGroup>
</template>

<style lang="scss" module>
.root {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.reactionAppearEnterActive,
.reactionAppearLeaveActive {
  transition:
    opacity 0.1s ease-in-out,
    transform 0.1s ease-in-out,
    margin-right 0.1s ease-in-out;
}

.reactionAppearEnterFrom,
.reactionAppearLeaveTo {
  opacity: 0;
  transform: scale(0.7);
  margin-right: -42px;
}

.newReaction {
  color: #ffffff80;
  background-color: #2b2d4b;
  padding: 2px 4px;
  border-radius: 2px;
  transition: color 0.1s;
  border: none;
  height: fit-content;
  display: flex;

  &:hover {
    color: white;
  }
}

.newReactionIcon {
  width: 20px;
  height: 20px;
}
</style>
